import { useFormContext, useWatch } from 'react-hook-form';
import type { CreateVisitType } from '../../../visits/entity';
import type { PreviewVisit } from '../../pages/booking-page.hooks';
import { normalizeDate } from '@features/visits/schedule/store';
import { useCustomerAvailableSlotsQuery } from '@features/visits/schedule/queries';
import { WorkingDay } from '@features/visits/schedule/store';
import { useCustomerAuth } from '@features/customer-reservations/CustomerAuthContext';
import {
  useHairdresserScheduleQuery,
  useReservationServicesQuery,
} from '@features/customer-reservations/queries';
import { useCustomerReservationHighlightedDays } from '@features/customer-reservations/store';
import { Service } from '@features/service/entity';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useMemo, useCallback, useEffect } from 'react';

export const useBookingCalendarSection = (
  setPreviewVisit: (v: PreviewVisit | undefined) => void,
) => {
  const { control, setValue, getValues } = useFormContext<CreateVisitType>();
  const { isAuthenticated } = useCustomerAuth();

  const selectedDate = useWatch({ control, name: 'date' });
  const serviceIds = useWatch({ control, name: 'serviceIds' });

  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const { data: scheduleData, isLoading: isLoadingSchedule } = useHairdresserScheduleQuery(
    isAuthenticated,
    currentMonth.year(),
    currentMonth.month() + 1,
  );
  const isScheduleConfigured = scheduleData?.isScheduleConfigured ?? false;

  const { data: servicesData } = useReservationServicesQuery(isAuthenticated);

  const selectedService = servicesData?.services?.find(s => s.id === serviceIds);

  const selectedDateDayjs = useMemo(
    () => (selectedDate ? dayjs(selectedDate) : undefined),
    [selectedDate],
  );

  const { getAvailableSlots, isLoading: isLoadingSlots } = useCustomerAvailableSlots(
    selectedDateDayjs,
    scheduleData?.userId,
    serviceIds ?? undefined,
  );

  const { processDayAvailability } = useCustomerReservationHighlightedDays(getAvailableSlots);

  const [highlightedDays, setHighlightedDays] = useState<
    Record<number, 'morning' | 'afternoon' | 'both'>
  >({});

  const [availableSlots, setAvailableSlots] = useState<{ start: string; end: string }[]>([]);

  const setCalculatedHighlightedDays = useCallback(
    (month: Dayjs, serviceId?: string) => {
      if (!scheduleData?.workingSchedule || !servicesData?.services) return;
      const targetService = servicesData.services.find(s => s.id === serviceId);
      if (!targetService) return;

      const availability: Record<number, 'morning' | 'afternoon' | 'both'> = {};
      for (let day = 1; day <= month.daysInMonth(); day++) {
        const dayAvailability = processDayAvailability(
          month.date(day),
          targetService,
          scheduleData.workingSchedule,
        );
        if (dayAvailability) availability[day] = dayAvailability;
      }
      setHighlightedDays(availability);
    },
    [scheduleData, servicesData, processDayAvailability],
  );

  useEffect(() => {
    if (serviceIds) setCalculatedHighlightedDays(currentMonth, serviceIds);
  }, [scheduleData, servicesData, serviceIds, currentMonth, setCalculatedHighlightedDays]);

  useEffect(() => {
    if (selectedDate && selectedService && scheduleData?.workingSchedule) {
      setAvailableSlots(
        getAvailableSlots(
          {
            workingSchedule: scheduleData.workingSchedule,
            targetDate: normalizeDate(dayjs(selectedDate)),
          },
          selectedService,
        ),
      );
    } else {
      setAvailableSlots([]);
    }
  }, [selectedDate, selectedService, scheduleData, getAvailableSlots]);

  const handleServiceChange = (serviceId: string) => {
    if (serviceId) setCalculatedHighlightedDays(currentMonth, serviceId);
  };

  const handleSlotClick = (slot: { start: string; end: string }) => {
    const currentDate = getValues('date');
    const [hours, minutes] = slot.start.split(':').map(Number);
    const [endHours, endMinutes] = slot.end.split(':').map(Number);
    setValue('date', dayjs(currentDate).hour(hours).minute(minutes).toDate());
    setValue('dateTo', dayjs(currentDate).hour(endHours).minute(endMinutes).toDate());
    setPreviewVisit({
      startTime: slot.start,
      endTime: slot.end,
      serviceName: servicesData?.services?.find(s => s.id === getValues('serviceIds'))?.serviceName,
    });
  };

  const handleCalendarChange = (newDate: Dayjs | null) => {
    if (newDate) {
      const d = newDate.hour(0).minute(0).second(0);
      setValue('date', d.toDate());
      setValue('dateTo', d.toDate());
      setPreviewVisit(undefined);
    }
  };

  const handleMonthChange = useCallback(
    (month: Dayjs) => {
      setValue('date', undefined);
      setValue('dateTo', undefined);
      setCurrentMonth(month);
      setPreviewVisit(undefined);
    },
    [setValue],
  );

  return {
    control,
    selectedDate,
    serviceIds,
    currentMonth,
    highlightedDays,
    availableSlots,
    isLoadingSlots,
    isLoadingSchedule,
    isScheduleConfigured,
    handleServiceChange,
    handleSlotClick,
    handleCalendarChange,
    handleMonthChange,
  };
};

const useCustomerAvailableSlots = (
  date: Dayjs | undefined,
  _userId: string | undefined,
  serviceId?: string,
) => {
  const dateStr = useMemo(() => (date ?? dayjs()).format('YYYY-MM-DD'), [date]);

  const { data: slots = [], isLoading } = useCustomerAvailableSlotsQuery(
    dateStr,
    serviceId,
    !!serviceId,
  );

  const getAvailableSlots = useCallback(
    (_workingDay: WorkingDay, _service: Service | undefined): { start: string; end: string }[] =>
      slots,
    [slots],
  );

  return { getAvailableSlots, isLoading };
};
